import {
  Component,
  input,
  output,
  signal,
  computed,
  HostBinding,
  HostListener,
  ElementRef,
  inject,
  DestroyRef,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type M3ToggleIconButtonSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl';
export type M3ToggleIconButtonShape = 'round' | 'square';
export type M3ToggleIconButtonVariant =
  'filled' | 'tonal' | 'outlined' | 'standard';
export type M3ToggleIconButtonWidth = 'narrow' | 'default' | 'wide';

@Component({
  selector: 'app-m3-toggle-icon-button',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button
      #buttonEl
      type="button"
      [class]="buttonClass()"
      [disabled]="disabled()"
      [attr.aria-label]="ariaLabel()"
      [attr.aria-describedby]="describedBy()"
      [attr.aria-pressed]="selected()"
      [attr.aria-expanded]="expanded()"
      (click)="handleClick($event)"
      (mousedown)="handleMouseDown()"
      (mouseup)="handleMouseUp()"
      (mouseleave)="handleMouseLeave()"
      (touchstart)="handleTouchStart()"
      (touchend)="handleTouchEnd()"
      (keydown)="handleKeyDown($event)"
      (keyup)="handleKeyUp($event)"
      (focus)="focused.emit()"
      (blur)="handleBlur()"
    >
      <span class="material-symbols-outlined" [attr.aria-hidden]="true">
        {{ selected() ? selectedIcon() : icon() }}
      </span>
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: inline-block;
      }

      :host(.enforce-touch-target) {
        min-width: 48px;
        min-height: 48px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      @keyframes press-ripple-spread {
        0% {
          transform: scale(0.6);
          opacity: 0;
        }
        100% {
          transform: scale(1);
          opacity: 0.1;
        }
      }

      @keyframes press-ripple-fade {
        0% {
          opacity: 0.1;
        }
        100% {
          opacity: 0;
        }
      }

      button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        cursor: pointer;
        transition:
          background-color var(--md-comp-button-motion-state-layer),
          color var(--md-comp-button-motion-state-layer),
          box-shadow var(--md-comp-button-motion-elevation),
          border-radius var(--md-comp-button-motion-shape-morph),
          transform var(--md-comp-button-motion-shape-morph);
        position: relative;
        font-family: inherit;
        font-size: 0;
        box-sizing: border-box;

        &::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          opacity: 0;
          pointer-events: none;
          transform: scale(1);
        }

        &:focus-visible,
        &.show-focus-ring {
          outline: none;
          box-shadow:
            0 0 0 2px var(--md-sys-color-surface),
            0 0 0 4px var(--md-sys-color-secondary);
        }

        &:focus:not(:focus-visible):not(.show-focus-ring) {
          outline: none;
        }

        // Size and width variants with shape morphing
        &.size-xs {
          --button-border-radius-resting: 9999px;
          --button-border-radius-pressed: 8px;
          --icon-opsz: 20;
          height: 32px;

          &.width-narrow {
            width: 28px;
            padding: 0 4px;
          }

          &.width-default {
            width: 32px;
          }

          &.width-wide {
            width: 40px;
            padding: 0 14px;
          }

          .material-symbols-outlined {
            font-size: 18px;
          }
        }

        &.size-sm {
          --button-border-radius-resting: 9999px;
          --button-border-radius-pressed: 10px;
          --icon-opsz: 24;
          height: 40px;

          &.width-narrow {
            width: 32px;
            padding: 0 4px;
          }

          &.width-default {
            width: 40px;
          }

          &.width-wide {
            width: 52px;
            padding: 0 14px;
          }

          .material-symbols-outlined {
            font-size: 24px;
          }
        }

        &.size-default {
          --button-border-radius-resting: 9999px;
          --button-border-radius-pressed: 14px;
          --icon-opsz: 24;
          height: 56px;

          &.width-narrow {
            width: 48px;
            padding: 0 4px;
          }

          &.width-default {
            width: 56px;
          }

          &.width-wide {
            width: 72px;
            padding: 0 14px;
          }

          .material-symbols-outlined {
            font-size: 28px;
          }
        }

        &.size-lg {
          --button-border-radius-resting: 9999px;
          --button-border-radius-pressed: 24px;
          --icon-opsz: 40;
          height: 96px;

          &.width-narrow {
            width: 64px;
            padding: 0 4px;
          }

          &.width-default {
            width: 96px;
          }

          &.width-wide {
            width: 128px;
            padding: 0 14px;
          }

          .material-symbols-outlined {
            font-size: 48px;
          }
        }

        &.size-xl {
          --button-border-radius-resting: 9999px;
          --button-border-radius-pressed: 34px;
          --icon-opsz: 48;
          height: 136px;

          &.width-narrow {
            width: 104px;
            padding: 0 4px;
          }

          &.width-default {
            width: 136px;
          }

          &.width-wide {
            width: 184px;
            padding: 0 14px;
          }

          .material-symbols-outlined {
            font-size: 64px;
          }
        }

        // Shape variants with selected state morph and press animation
        &.shape-round {
          border-radius: var(--button-border-radius-resting);

          &.selected {
            border-radius: var(--md-comp-icon-button-shape-square);
          }

          &.pressing:not(.selected) {
            border-radius: var(--button-border-radius-pressed);
            transform: scale(0.96);
          }

          &.pressing.selected {
            border-radius: calc(var(--md-comp-icon-button-shape-square) * 0.6);
            transform: scale(0.96);
          }
        }

        &.shape-square {
          border-radius: var(--md-comp-icon-button-shape-square);

          &.selected {
            border-radius: var(--button-border-radius-resting);
          }

          &.pressing:not(.selected) {
            border-radius: calc(var(--md-comp-icon-button-shape-square) * 0.6);
            transform: scale(0.96);
          }

          &.pressing.selected {
            border-radius: var(--button-border-radius-pressed);
            transform: scale(0.96);
          }
        }

        // Variant styles - Filled (default)
        &.variant-filled {
          // Unselected state
          background: var(--md-sys-color-surface-container);
          color: var(--md-sys-color-on-surface-variant);

          &::before {
            background: var(--md-sys-color-on-surface-variant);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }

          // Selected state
          &.selected {
            background: var(--md-sys-color-primary);
            color: var(--md-sys-color-on-primary);

            &::before {
              background: var(--md-sys-color-on-primary);
            }

            &:hover:not(:disabled) {
              box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

              &::before {
                opacity: 0.08;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 500,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.pressing:not(:disabled) {
              box-shadow: none;

              &::before {
                opacity: 0.1;
                animation: press-ripple-spread 150ms
                  var(--md-sys-motion-easing-expressive) forwards;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 300,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.press-release:not(:disabled) {
              &::before {
                animation: press-ripple-fade 200ms
                  var(--md-sys-motion-easing-standard) forwards;
              }
            }

            &:focus-visible:not(:disabled) {
              &::before {
                opacity: 0.1;
              }
            }
          }
        }

        // Tonal variant
        &.variant-tonal {
          // Unselected state
          background: var(--md-sys-color-surface-container);
          color: var(--md-sys-color-on-surface-variant);

          &::before {
            background: var(--md-sys-color-on-surface-variant);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }

          // Selected state
          &.selected {
            background: var(--md-sys-color-secondary-container);
            color: var(--md-sys-color-on-secondary-container);

            &::before {
              background: var(--md-sys-color-on-secondary-container);
            }

            &:hover:not(:disabled) {
              box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

              &::before {
                opacity: 0.08;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 500,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.pressing:not(:disabled) {
              box-shadow: none;

              &::before {
                opacity: 0.1;
                animation: press-ripple-spread 150ms
                  var(--md-sys-motion-easing-expressive) forwards;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 300,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.press-release:not(:disabled) {
              &::before {
                animation: press-ripple-fade 200ms
                  var(--md-sys-motion-easing-standard) forwards;
              }
            }

            &:focus-visible:not(:disabled) {
              &::before {
                opacity: 0.1;
              }
            }
          }
        }

        // Outlined variant
        &.variant-outlined {
          // Unselected state
          background: transparent;
          color: var(--md-sys-color-on-surface-variant);
          border: 1px solid var(--md-sys-color-outline-variant);

          &::before {
            background: var(--md-sys-color-on-surface-variant);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }

          // Selected state
          &.selected {
            background: var(--md-sys-color-inverse-surface);
            color: var(--md-sys-color-inverse-on-surface);
            border-color: var(--md-sys-color-outline-variant);

            &::before {
              background: var(--md-sys-color-inverse-on-surface);
            }

            &:hover:not(:disabled) {
              &::before {
                opacity: 0.08;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 500,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.pressing:not(:disabled) {
              &::before {
                opacity: 0.1;
                animation: press-ripple-spread 150ms
                  var(--md-sys-motion-easing-expressive) forwards;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 300,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.press-release:not(:disabled) {
              &::before {
                animation: press-ripple-fade 200ms
                  var(--md-sys-motion-easing-standard) forwards;
              }
            }

            &:focus-visible:not(:disabled) {
              &::before {
                opacity: 0.1;
              }
            }
          }
        }

        // Standard variant
        &.variant-standard {
          // Unselected state
          background: transparent;
          color: var(--md-sys-color-on-surface-variant);

          &::before {
            background: var(--md-sys-color-on-surface-variant);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }

          // Selected state
          &.selected {
            background: transparent;
            color: var(--md-sys-color-primary);

            &::before {
              background: var(--md-sys-color-primary);
            }

            &:hover:not(:disabled) {
              &::before {
                opacity: 0.08;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 500,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.pressing:not(:disabled) {
              &::before {
                opacity: 0.1;
                animation: press-ripple-spread 150ms
                  var(--md-sys-motion-easing-expressive) forwards;
              }

              .material-symbols-outlined {
                font-variation-settings:
                  'FILL' 1,
                  'wght' 300,
                  'GRAD' 0,
                  'opsz' var(--icon-opsz);
              }
            }

            &.press-release:not(:disabled) {
              &::before {
                animation: press-ripple-fade 200ms
                  var(--md-sys-motion-easing-standard) forwards;
              }
            }

            &:focus-visible:not(:disabled) {
              &::before {
                opacity: 0.1;
              }
            }
          }
        }

        // Disabled state
        &:disabled {
          cursor: not-allowed;

          &::before {
            display: none !important;
          }

          &.variant-filled {
            background: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.12
            ) !important;
            color: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.38
            ) !important;
          }

          &.variant-tonal {
            background: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.12
            ) !important;
            color: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.38
            ) !important;
          }

          &.variant-outlined {
            background: transparent !important;
            color: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.38
            ) !important;
            border-color: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.38
            ) !important;
          }

          &.variant-standard {
            background: transparent !important;
            color: rgba(
              from var(--md-sys-color-on-surface) r g b / 0.38
            ) !important;
          }
        }
      }

      .material-symbols-outlined {
        font-variation-settings:
          'FILL' 0,
          'wght' 400,
          'GRAD' 0,
          'opsz' var(--icon-opsz, 24);
        transition: font-variation-settings
          var(--md-sys-motion-expressive-effects-fast);
      }

      button.selected .material-symbols-outlined {
        font-variation-settings:
          'FILL' 1,
          'wght' 400,
          'GRAD' 0,
          'opsz' var(--icon-opsz, 24);
      }

      @media (prefers-reduced-motion: reduce) {
        button {
          transition: none;

          &::before {
            animation: none !important;
          }

          &.pressing {
            transform: none;
          }
        }

        .material-symbols-outlined {
          transition: none;
        }
      }
    `,
  ],
})
export class M3ToggleIconButtonComponent {
  private focusMonitor = inject(FocusMonitor);
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  private buttonEl = viewChild<ElementRef<HTMLButtonElement>>('buttonEl');

  /**
   * Makes the host focusable so MatMenuTrigger can restore focus to it.
   * The HostListener below then delegates focus to the inner button.
   */
  @HostBinding('attr.tabindex')
  get hostTabIndex(): number {
    return -1;
  }

  /**
   * Delegates focus from host to inner button.
   * This is needed because MatMenuTrigger restores focus to its host element,
   * but the actual focusable element is the inner button.
   */
  @HostListener('focus')
  onHostFocus(): void {
    this.buttonEl()?.nativeElement?.focus();
  }

  icon = input.required<string>();
  selectedIcon = input<string>();
  variant = input<M3ToggleIconButtonVariant>('filled');
  size = input<M3ToggleIconButtonSize>('default');
  shape = input<M3ToggleIconButtonShape>('round');
  width = input<M3ToggleIconButtonWidth>('default');
  selected = input<boolean>(false);
  disabled = input<boolean>(false);
  expanded = input<boolean>(false);
  enforceMinTouchTarget = input<boolean>(true);

  ariaLabel = input.required<string>();
  describedBy = input<string>();

  clicked = output<MouseEvent>();
  toggled = output<boolean>();
  focused = output<void>();
  blurred = output<void>();

  private isPressing = signal(false);
  private isReleasing = signal(false);
  private pressAnimationActive = signal(false);
  private focusOrigin: FocusOrigin = null;
  private showFocusRing = signal(false);

  constructor() {
    this.focusMonitor
      .monitor(this.elementRef, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((origin) => {
        this.focusOrigin = origin;
        this.showFocusRing.set(origin === 'keyboard' || origin === 'program');
      });
  }

  @HostBinding('class.enforce-touch-target')
  get shouldEnforceTouchTarget(): boolean {
    const size = this.size();
    return this.enforceMinTouchTarget() && (size === 'xs' || size === 'sm');
  }

  buttonClass = computed(() => {
    const pressing = this.isPressing();
    const releasing = this.isReleasing();
    const showRing = this.showFocusRing();

    const classes = [
      'toggle-icon-button',
      `size-${this.size()}`,
      `shape-${this.shape()}`,
      `width-${this.width()}`,
      `variant-${this.variant()}`,
    ];

    if (this.selected()) {
      classes.push('selected');
    }

    if (pressing) {
      classes.push('pressing');
    }

    if (releasing) {
      classes.push('press-release');
    }

    if (showRing) {
      classes.push('show-focus-ring');
    }

    return classes.join(' ');
  });

  handleClick(event: MouseEvent): void {
    this.clicked.emit(event);
    this.toggled.emit(!this.selected());
  }

  handleMouseDown(): void {
    if (!this.disabled()) {
      this.isPressing.set(true);
      this.pressAnimationActive.set(true);
    }
  }

  handleMouseUp(): void {
    this.isPressing.set(false);
    this.isReleasing.set(true);

    setTimeout(() => {
      this.isReleasing.set(false);
      this.pressAnimationActive.set(false);
    }, 350);
  }

  handleMouseLeave(): void {
    if (this.isPressing()) {
      this.isPressing.set(false);
      this.isReleasing.set(true);

      setTimeout(() => {
        this.isReleasing.set(false);
        this.pressAnimationActive.set(false);
      }, 350);
    }
  }

  handleTouchStart(): void {
    if (!this.disabled()) {
      this.isPressing.set(true);
      this.pressAnimationActive.set(true);
    }
  }

  handleTouchEnd(): void {
    this.isPressing.set(false);
    this.isReleasing.set(true);

    setTimeout(() => {
      this.isReleasing.set(false);
      this.pressAnimationActive.set(false);
    }, 350);
  }

  handleKeyDown(event: KeyboardEvent): void {
    if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) {
      if (!this.disabled()) {
        this.isPressing.set(true);
        this.pressAnimationActive.set(true);
      }
    }
  }

  handleBlur(): void {
    // Enter can move focus away (e.g. opening a dialog), so keyup never reaches this button
    this.isPressing.set(false);
    this.pressAnimationActive.set(false);
    this.blurred.emit();
  }

  handleKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.isPressing.set(false);
      this.isReleasing.set(true);

      setTimeout(() => {
        this.isReleasing.set(false);
        this.pressAnimationActive.set(false);
      }, 350);
    }
  }
}
